import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import { CheckCircle as CheckIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';

const Checkout: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sourceParam = searchParams.get('source') || (typeof window !== 'undefined' ? sessionStorage.getItem('checkoutSource') : null) || 'landing';
  const initialSelectedPlan = typeof window !== 'undefined' ? sessionStorage.getItem('selectedPlan') : null;
  const shouldAutoRedirect = Boolean(initialSelectedPlan) && sourceParam !== 'connect';

  const [loading, setLoading] = useState(shouldAutoRedirect);
  const [error, setError] = useState('');
  const [selectedPlan, setSelectedPlan] = useState<string | null>(initialSelectedPlan);

  const handleCheckout = async (planToUse?: string) => {
    const plan = planToUse || selectedPlan || (typeof window !== 'undefined' ? sessionStorage.getItem('selectedPlan') : null);
    
    if (!plan) {
      setError('No plan selected');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const baseUrl = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8080';
      const response = await fetch(`${baseUrl}/api/billing/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ plan: plan })
      });

      const data = await response.json();
      
      if (data?.url) {
        // Keep loading state until browser navigates to Stripe
        window.location.href = data.url;
      } else {
        setError(data?.error || 'Failed to create checkout session');
        setLoading(false);
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
      console.error('Checkout error:', err);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sourceParam === 'connect') {
      setSelectedPlan(null);
      setLoading(false);
    } else if (initialSelectedPlan) {
      // Auto-redirect without flashing the pricing grid
      handleCheckout(initialSelectedPlan);
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const planDetails = {
    starter: {
      name: 'Starter',
      price: '$4.99',
      description: 'Perfect for small businesses',
      features: [
        'Up to 1,000 messages/month',
        'Basic AI responses',
        'Email support',
        'Dashboard analytics'
      ]
    },
    professional: {
      name: 'Professional',
      price: '$9.99',
      description: 'Ideal for growing businesses',
      features: [
        'Up to 10,000 messages/month',
        'Advanced AI responses',
        'Priority support',
        'Advanced analytics',
        'Custom prompts'
      ]
    },
    enterprise: {
      name: 'Enterprise',
      price: '$49.99',
      description: 'For large organizations',
      features: [
        'Unlimited messages',
        'Custom AI training',
        '24/7 phone support',
        'Advanced integrations',
        'Dedicated account manager'
      ]
    }
  };

  const currentPlan = selectedPlan ? planDetails[selectedPlan as keyof typeof planDetails] : null;

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={40} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Redirecting to payment...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!currentPlan) {
    // If no plan selected, show subscription required message
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            🚀 Unlock Premium Features
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
            To connect your social media accounts and access our AI-powered management tools, you'll need an active subscription.
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, pt: 3, overflow: 'visible' }}>
          {Object.entries(planDetails).map(([key, plan]) => (
            <Card key={key} sx={{ 
              position: 'relative',
              border: key === 'professional' ? '2px solid #1976d2' : '1px solid rgba(0,0,0,0.12)',
              transform: key === 'professional' ? 'scale(1.05)' : 'scale(1)',
              zIndex: key === 'professional' ? 10 : 1,
              mt: key === 'professional' ? 2 : 3,
              overflow: 'visible',
            }}>
              {key === 'professional' && (
                <Chip 
                  label="Most Popular" 
                  color="primary" 
                  size="small"
                  sx={{ 
                    position: 'absolute',
                    top: -16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 20,
                    fontWeight: 600,
                    fontSize: '0.75rem'
                  }}
                />
              )}
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h5" component="h2" gutterBottom>
                  {plan.name}
                </Typography>
                <Typography variant="h3" color="primary" gutterBottom>
                  {plan.price}
                  <Typography component="span" variant="body2" color="text.secondary">
                    /month
                  </Typography>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {plan.description}
                </Typography>
                <Box sx={{ mb: 3 }}>
                  {plan.features.map((feature, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: 'flex-start' }}>
                      <CheckIcon sx={{ color: 'success.main', mr: 1, fontSize: 18 }} />
                      <Typography variant="body2" sx={{ textAlign: 'left' }}>{feature}</Typography>
                    </Box>
                  ))}
                </Box>
                <Box
                  sx={{
                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(5, 150, 105, 0.15))',
                    border: '1px solid rgba(16, 185, 129, 0.3)',
                    borderRadius: 1.5,
                    px: 1.5,
                    py: 0.5,
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#059669',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                    }}
                  >
                    🎉 7 Days Free • Then {plan.price}/mo
                  </Typography>
                </Box>
                <Button
                  variant={key === 'professional' ? 'contained' : 'outlined'}
                  fullWidth
                  onClick={() => {
                    sessionStorage.setItem('selectedPlan', key);
                    setLoading(true);
                    handleCheckout(key);
                  }}
                  disabled={loading}
                  sx={{ py: 1.5 }}
                >
                  {loading ? <CircularProgress size={20} /> : 'Get Started'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </Box>

        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography variant="body2" color="text.secondary">
            ✨ Start connecting your social media accounts and managing them with AI-powered automation
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => setSelectedPlan(null)}
        sx={{ mb: 2 }}
      >
        Choose a different plan
      </Button>
      <Typography variant="h4" component="h1" gutterBottom align="center">
        Complete Your Purchase
      </Typography>
      
      <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
        Review your selected plan and proceed to payment
      </Typography>

      <Card sx={{ maxWidth: 600, mx: 'auto' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" component="h2" sx={{ flex: 1 }}>
              {currentPlan.name}
            </Typography>
            <Chip 
              label="Selected" 
              color="primary" 
              size="small"
            />
          </Box>

          <Typography variant="h3" color="primary" gutterBottom>
            {currentPlan.price}
            <Typography component="span" variant="body2" color="text.secondary">
              /month
            </Typography>
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {currentPlan.description}
          </Typography>

          <Typography variant="h6" gutterBottom>
            What's included:
          </Typography>

          <Box sx={{ mb: 3 }}>
            {currentPlan.features.map((feature, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CheckIcon sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
                <Typography variant="body2">{feature}</Typography>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(5, 150, 105, 0.15))',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: 1.5,
              px: 1.5,
              py: 0.5,
              mb: 2,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: '#059669',
                fontWeight: 600,
                fontSize: '0.7rem',
              }}
            >
              🎉 7 Days Free • Then {currentPlan.price}/mo
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={() => handleCheckout()}
            disabled={loading}
            sx={{ py: 1.5 }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Proceed to Payment'
            )}
          </Button>

          <Typography variant="caption" color="text.secondary" align="center" sx={{ display: 'block', mt: 2 }}>
            You will be redirected to Stripe for secure payment processing
          </Typography>
        </CardContent>
      </Card>
    </Container>
  );
};

export default Checkout;
